<template>
  <input
    type="text"
    class="input"
    :classes="{ 'is-danger': !fieldData.valid, 'is-loading': fieldData.loading }"
    disabled
    v-if="!corpusId || !fieldDefinition.editable"
    :value="readOnlyLabel"
  />
  <template v-else>
    <div class="columns m-0">
      <div class="column p-0">
        <label class="label">Use the current state of the project (default)</label>
      </div>
      <div class="column p-0 is-narrow">
        <button
          class="button is-small"
          :class="{ 'is-primary': fieldData.value === null }"
          v-on:click="fieldData.value = null"
          :disabled="fieldData.loading || undefined"
        >
          Select
        </button>
      </div>
    </div>
    <label class="label">Use an existing database export</label>
    <Paginator
      :response="exportStore.corpusExports[corpusId]"
      :loading="paginationLoading"
      singular="export"
      plural="exports"
      v-model:page="page"
      :page-size="10"
    >
      <template v-slot:no-results>
        <div class="notification is-warning mb-2">No existing database exports to display.</div>
      </template>
      <template v-slot:default="{ results }">
        <table class="table is-hoverable is-fullwidth mb-2">
          <thead>
            <tr>
              <th>Creator</th>
              <th v-if="authStore.hasFeature('enterprise')">Source</th>
              <th>State</th>
              <th>Updated</th>
              <th class="is-narrow">Actions</th>
            </tr>
          </thead>
          <tbody>
            <tr
              v-for="corpusExport in results as CorpusExport[]"
              :key="corpusExport.id"
            >
              <td>{{ corpusExport.user.display_name }}</td>
              <td v-if="authStore.hasFeature('enterprise')">{{ corpusExport.source }}</td>
              <td>{{ EXPORT_STATES[corpusExport.state] }}</td>
              <td :title="corpusExport.updated">{{ ago(new Date(corpusExport.updated)) }}</td>
              <td class="is-narrow">
                <div class="field">
                  <button
                    class="button is-small"
                    :class="{ 'is-primary': fieldData.value === corpusExport.id }"
                    v-on:click="fieldData.value = corpusExport.id"
                    :disabled="
                      corpusExport.state === 'failed' ||
                      paginationLoading ||
                      fieldData.loading ||
                      undefined
                    "
                    :title="selectTitle(corpusExport)"
                  >
                    Select
                  </button>
                </div>
              </td>
            </tr>
          </tbody>
        </table>
      </template>
    </Paginator>
  </template>
  <p
    class="help is-danger"
    v-for="(error, i) in [...localErrors, ...fieldData.errors]"
    :key="i"
  >
    {{ error }}
  </p>
</template>

<script setup lang="ts">
import { computed, ref, watch } from 'vue'

import Paginator from '@/components/Paginator.vue'
import { EXPORT_STATES, UUID_REGEX } from '@/config'
import { ago, errorParser } from '@/helpers'
import { useAuthStore, useExportStore } from '@/stores'
import type { CorpusExport } from '@/types/export'

import { type FieldModel, type FieldProps, ValidationError, defineValidator } from '.'

type Constraint = { type: 'corpus_export'; many: false }

const { fieldDefinition, corpusId } = defineProps<FieldProps<Constraint>>()
const fieldData = defineModel<FieldModel<Constraint>>({ required: true })

async function validator(id: unknown) {
  if (id === null) return
  if (typeof id !== 'string' || !UUID_REGEX.test(id))
    throw new ValidationError([`${id} is not a valid project export ID`])

  // When this field is loaded without a corpus ID set, we cannot validate anything
  if (!corpusId) return

  // Load a page of corpus exports if necessary
  if (!exportStore.corpusExports[corpusId]?.results) {
    try {
      await exportStore.list(corpusId)
    } catch (err) {
      throw new ValidationError([`Could not retrieve this project export: ${errorParser(err)}`])
    }
  }

  const corpusExport = exportStore.corpusExports[corpusId].results.find(
    (corpusExport) => corpusExport.id === id,
  )
  // Because this is only one page, we might not find the export, and will not be able to tell if it's invalid
  if (!corpusExport) return

  if (corpusExport.state === 'failed') throw new ValidationError(['This export has failed'])
}
const { localErrors } = defineValidator(fieldDefinition, fieldData.value, validator)

const authStore = useAuthStore()
const exportStore = useExportStore()

const paginationLoading = ref(false)
const page = ref(1)

watch(
  page,
  async (value) => {
    if (paginationLoading.value || !corpusId) return
    paginationLoading.value = true
    try {
      await exportStore.list(corpusId, value)
    } finally {
      paginationLoading.value = false
    }
  },
  { immediate: true },
)

function selectTitle(corpusExport: CorpusExport): string {
  if (corpusExport.state === 'failed') return 'This export cannot be selected as it has failed'
  return 'Use this export'
}

const readOnlyLabel = computed<string>(() => {
  if (!fieldData.value.value) return 'Current state of the project'
  else return `Project export ${fieldData.value.value}`
})
</script>
